import React from "react";
import { MoreHorizontal, ExternalLink, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type ArtistApplication } from "@/hooks/useArtistApplications";

interface ArtistApplicationsTableProps {
  applications: ArtistApplication[];
  onView: (application: ArtistApplication) => void;
  onDelete: (applicationId: string) => void;
  onStatusChange: (applicationId: string, status: ArtistApplication['status']) => void;
}

const getStatusColor = (status: ArtistApplication['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'reviewed':
      return 'bg-blue-100 text-blue-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: ArtistApplication['status']) => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'reviewed':
      return 'Reviewed';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return status;
  }
};

export default function ArtistApplicationsTable({
  applications,
  onView,
  onDelete,
  onStatusChange,
}: ArtistApplicationsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Artist</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Art Type</TableHead>
          <TableHead>Portfolio</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Applied</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {applications.map((application) => (
          <TableRow key={application.id}>
            <TableCell>
              <div className="flex items-center space-x-3">
                <div className="flex flex-col space-y-1">
                  <div className="font-medium">{application.full_name}</div>
                  <div className="text-sm text-gray-500">
                    {application.bio.substring(0, 50)}
                    {application.bio.length > 50 && "..."}
                  </div>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-sm">
                  <Mail className="h-3 w-3" />
                  <span className="text-gray-600">{application.email}</span>
                </div>
                {application.phone && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="h-3 w-3" />
                    <span className="text-gray-600">{application.phone}</span>
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="text-sm">
                <div className="font-medium">{application.art_type}</div>
                <div className="text-gray-500">{application.collaboration_model}</div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(application.portfolio_link, '_blank')}
                  className="h-8 px-2"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View
                </Button>
              </div>
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Badge 
                    className={`cursor-pointer ${getStatusColor(application.status)}`}
                  >
                    {getStatusLabel(application.status)}
                  </Badge>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onStatusChange(application.id, 'pending')}>
                    Pending
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange(application.id, 'reviewed')}>
                    Reviewed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange(application.id, 'approved')}>
                    Approved
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange(application.id, 'rejected')}>
                    Rejected
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
            <TableCell>
              <div className="text-sm text-gray-500">
                {new Date(application.created_at).toLocaleDateString()}
              </div>
            </TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => onView(application)}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(application.portfolio_link, '_blank')}>
                            View Portfolio
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onDelete(application.id)}
                            className="text-red-600"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
